import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import mermaid from "mermaid";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDiagramProject,
  faCode,
  faExpand,
  faCompress,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";

import CopyButton from "../Message/CopyButton";
import styles from "./Markdown.module.scss";

type MermaidTheme = "dark" | "default";

function getAppTheme(): MermaidTheme {
  const root = document.documentElement;
  if (root.classList.contains("theme--dark")) return "dark";
  if (root.classList.contains("theme--light")) return "default";
  // System mode: no theme class, defer to OS preference
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "default";
}

interface MermaidDiagramProps {
  code: string;
  /**
   * When true, the diagram stays in source view until streaming ends to
   * avoid running mermaid.parse() against a partial diagram.
   */
  isStreaming?: boolean;
}

// Inject CSS into the sandboxed iframe's inner document to override the SVG
// background rect. mermaid sets that rect's fill as an inline attribute which
// themeVariables cannot reliably override, so we reach inside via base64
// manipulation.
function injectIframeBackground(html: string, color: string): string {
  const match = /src="(data:text\/html[^"]*;base64,)([^"]+)"/.exec(html);
  if (!match) return html;

  const decoded = atob(match[2]);
  const css = `<style>html,body{background:${color}!important}svg>rect:first-child{fill:${color}!important}</style>`;
  const modified = decoded.includes("</head>")
    ? decoded.replace("</head>", `${css}</head>`)
    : css + decoded;

  return html.replace(match[2], btoa(modified));
}

// In sandbox mode mermaid returns an <iframe> with a base64 data URL containing
// the SVG. The iframe has width="100%" which causes it to fill its container
// instead of rendering at natural size. Decode the base64 content, extract the
// viewBox dimensions, and return the natural pixel width.
function extractNaturalWidth(html: string): number | null {
  const b64Match = /src="data:text\/html[^"]*;base64,([^"]+)"/.exec(html);
  if (b64Match) {
    const decoded = atob(b64Match[1]);
    const vbMatch = /viewBox="[-\d.]+\s+[-\d.]+\s+([\d.]+)\s+[\d.]+"/.exec(
      decoded
    );
    if (vbMatch) return parseFloat(vbMatch[1]);
  }
  const vbMatch = /viewBox="[-\d.]+\s+[-\d.]+\s+([\d.]+)\s+[\d.]+"/.exec(html);
  if (vbMatch) return parseFloat(vbMatch[1]);
  return null;
}

// Extract the SVG markup from the sandbox iframe's base64 data URL,
// render it to a canvas at 2x resolution, and trigger a PNG download.
function downloadDiagramAsPng(html: string) {
  const b64Match = /src="data:text\/html[^"]*;base64,([^"]+)"/.exec(html);
  if (!b64Match) return;
  const decoded = atob(b64Match[1]);
  const svgMatch = /<svg[\s\S]*<\/svg>/.exec(decoded);
  if (!svgMatch) return;

  const svgString = svgMatch[0];
  const widthMatch = /viewBox="[-\d.]+\s+[-\d.]+\s+([\d.]+)\s+([\d.]+)"/.exec(
    svgString
  );
  if (!widthMatch) return;
  const vbWidth = parseFloat(widthMatch[1]);
  const vbHeight = parseFloat(widthMatch[2]);

  const scale = 2;
  const canvas = document.createElement("canvas");
  canvas.width = vbWidth * scale;
  canvas.height = vbHeight * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const img = new Image();
  img.onload = () => {
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const link = document.createElement("a");
    link.download = "diagram.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  // Encode UTF-8 SVG to base64 via TextEncoder to avoid deprecated unescape.
  const utf8Bytes = new TextEncoder().encode(svgString);
  let binary = "";
  for (let i = 0; i < utf8Bytes.length; i++) {
    binary += String.fromCharCode(utf8Bytes[i]);
  }
  img.src = `data:image/svg+xml;base64,${btoa(binary)}`;
}

export default React.memo(function MermaidDiagram({
  code,
  isStreaming = false,
}: MermaidDiagramProps) {
  const id = useId().replace(/:/g, "");
  const renderCount = useRef(0);
  const diagramRef = useRef<HTMLDivElement>(null);
  // Hidden container for mermaid.render() to place its temporary iframe into.
  // Without this, mermaid appends a full-viewport iframe to document.body which
  // causes a brief scrollbar flash.
  const renderContainerRef = useRef<HTMLDivElement>(null);
  // Tracks whether the pending render was explicitly user-requested so that
  // parse failures are shown as errors rather than suppressed silently.
  const userRequested = useRef(false);
  // null = follow streaming-end latch. true/false = explicit user override
  // that sticks for the lifetime of this component. Silent auto-render
  // failures also set this to false to stay in source view.
  const [userOverride, setUserOverride] = useState<boolean | null>(null);
  // Latched: once streaming has ended at least once, stays true even if a
  // new message starts streaming. Render-phase update — see
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [streamingEnded, setStreamingEnded] = useState(!isStreaming);
  if (!isStreaming && !streamingEnded) {
    setStreamingEnded(true);
  }
  const showDiagram = userOverride !== null ? userOverride : streamingEnded;
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mermaidTheme, setMermaidTheme] = useState<MermaidTheme>(getAppTheme);
  const [fullSize, setFullSize] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const update = () => setMermaidTheme(getAppTheme());

    // Watch for explicit theme class changes (light/dark toggle)
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Watch for OS preference changes (system mode)
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", update);

    return () => {
      observer.disconnect();
      mq.removeEventListener("change", update);
    };
  }, []);

  useEffect(() => {
    if (!showDiagram) return;

    const wasUserRequested = userRequested.current;
    userRequested.current = false;

    // Discard results from stale render attempts so out-of-order promise
    // resolutions cannot cause the SVG to flicker.
    let cancelled = false;

    renderCount.current += 1;
    const elementId = `mermaid-${id}-${renderCount.current}`;

    // background / mainBkg must match --foreground from _colors.scss since CSS
    // variables cannot be read inside the sandboxed iframe at render time.
    const background = mermaidTheme === "dark" ? "#20283e" : "#e5f1fa";
    // Dark theme's default cScale0 is near-black, invisible on our background.
    // Override section colors so all diagram branches (mindmaps, etc.) are legible.
    const darkSectionColors =
      mermaidTheme === "dark"
        ? {
            cScale0: "#3b5998",
            cScale1: "#8b2252",
            cScale2: "#2e7d6e",
            cScale3: "#8b6914",
            cScale4: "#6a4c93",
            cScale5: "#1b7a4a",
            cScale6: "#a0522d",
            cScale7: "#4682b4",
          }
        : {};
    mermaid.initialize({
      startOnLoad: false,
      theme: mermaidTheme,
      securityLevel: "sandbox",
      themeVariables: {
        background,
        mainBkg: background,
        ...darkSectionColors,
      },
    });
    mermaid
      .parse(code)
      .then(() => mermaid.render(elementId, code, renderContainerRef.current!))
      .then(({ svg: renderedSvg }) => {
        if (cancelled) return;
        setSvg(injectIframeBackground(renderedSvg, background));
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSvg(null);
        if (wasUserRequested) {
          setError(err instanceof Error ? err.message : String(err));
        } else {
          setError(null);
          // Silent failure during auto-render: stick on source view so the
          // next streaming change or user click is required to retry.
          setUserOverride(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [code, id, mermaidTheme, showDiagram]);

  // Track the container width so we know if the diagram is wider than
  // its container (and thus zoomable). Uses a ResizeObserver on the
  // scroll wrapper (.mermaidDiagram) via callback ref so observation
  // starts when the element mounts (which may be after initial render).
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const scrollParentRef = useCallback((el: HTMLDivElement | null) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (el) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });
      observer.observe(el);
      resizeObserverRef.current = observer;
    }
  }, []);

  // Derive natural width and zoomability from current state — no DOM
  // manipulation needed.
  const naturalWidth = svg ? extractNaturalWidth(svg) : null;
  const zoomable = !!naturalWidth && naturalWidth > containerWidth;

  // Hidden container that mermaid.render() uses for its temporary iframe so it
  // never touches document.body. Must be present in every render path.
  const renderContainer = (
    <div
      ref={renderContainerRef}
      style={{
        position: "fixed",
        top: -9999,
        left: -9999,
        width: "100vw",
        height: "100vh",
        opacity: 0,
        pointerEvents: "none",
      }}
    />
  );

  if (!showDiagram || (!svg && !error)) {
    return (
      <div className={styles.mermaidContainer}>
        {renderContainer}
        <div className={styles.mermaidHeader}>
          <button
            className={styles.mermaidToggle}
            onClick={() => {
              userRequested.current = true;
              setUserOverride(true);
            }}
          >
            <FontAwesomeIcon icon={faDiagramProject} />
            Render Diagram
          </button>
        </div>
        <div className={styles.codeBlock}>
          <SyntaxHighlighter
            style={oneDark}
            language="mermaid"
            customStyle={{ borderRadius: "0 0 0.25rem 0.25rem", margin: "0px" }}
            PreTag="div"
          >
            {code}
          </SyntaxHighlighter>
          <CopyButton content={code} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.mermaidContainer}>
        {renderContainer}
        <div className={styles.mermaidHeader}>
          <button
            className={styles.mermaidToggle}
            onClick={() => setUserOverride(false)}
          >
            <FontAwesomeIcon icon={faCode} />
            View Source
          </button>
        </div>
        <div className={styles.mermaidError}>Mermaid render error: {error}</div>
      </div>
    );
  }

  return (
    <div className={styles.mermaidContainer}>
      {renderContainer}
      <div className={styles.mermaidHeader}>
        <button
          className={styles.mermaidToggle}
          onClick={() => setUserOverride(false)}
        >
          <FontAwesomeIcon icon={faCode} />
          View Source
        </button>
        {zoomable && (
          <button
            className={styles.mermaidToggle}
            onClick={() => setFullSize((v) => !v)}
          >
            <FontAwesomeIcon icon={fullSize ? faCompress : faExpand} />
            {fullSize ? "Fit to Width" : "Full Size"}
          </button>
        )}
        <button
          className={styles.mermaidToggle}
          style={{ marginLeft: "auto" }}
          onClick={() => downloadDiagramAsPng(svg!)}
        >
          <FontAwesomeIcon icon={faDownload} />
          Download
        </button>
      </div>
      <div ref={scrollParentRef} className={styles.mermaidDiagram}>
        <div
          ref={diagramRef}
          className={styles.mermaidDiagramInner}
          style={
            fullSize && naturalWidth
              ? { minWidth: `${naturalWidth}px` }
              : undefined
          }
          dangerouslySetInnerHTML={{ __html: svg! }}
        />
      </div>
    </div>
  );
});
